import { AfterViewInit, Component, OnInit, inject } from "@angular/core";
import { createEmpty, extend } from "ol/extent";
import { fromLonLat, toLonLat } from "ol/proj";
import {
  Attribution,
  defaults as defaultControls,
  FullScreen,
  ScaleLine
} from "ol/control";
import {
  defaults as defaultInteractions,
  DragRotateAndZoom,
  Draw,
  Modify,
  Select
} from "ol/interaction";
import Map from "ol/Map";
import View from "ol/View";
import XYZ from "ol/source/XYZ";
import { Tile as TileLayer, Vector as VectorLayer } from "ol/layer";
import { BingMaps, Vector as VectorSource } from "ol/source";
import { Circle as CircleStyle, Fill, Stroke, Style, Text } from "ol/style";
import Feature from "ol/Feature";
import WKT from "ol/format/WKT";
import { ZoneStore } from "@app/services/store/zone-store";
import { Zone } from "@app/models/rest/zone";
import { MatDialog } from "@angular/material/dialog";
import { ZoneEditOverlayComponent } from "@app/view/setup/resourcing/zone/zone-edit-overlay/zone-edit-overlay.component";
import { LocationModel } from "@app/models/rest/location";
import { Point } from "ol/geom";
import { NgxUiLoaderService, NgxUiLoaderModule } from "ngx-ui-loader";
import { NewZoneConfirmDialogComponent } from "@app/view/setup/resourcing/zone/new-zone-confirm-dialog/new-zone-confirm-dialog.component";
import {
  ZonePostcodeLookupComponent,
  ZonePostcodeLookupResult
} from "@app/view/setup/resourcing/zone/zone-postcode-lookup/zone-postcode-lookup.component";
import { ServiceErrorHandlerService } from "@app/view/shared/service-error-handler.service";
import { ClientConfigService } from "@app/core/config/client-config.service";
import { ResourceCollection } from "@lagoshny/ngx-hateoas-client";
import { MatToolbar } from "@angular/material/toolbar";
import {
  MatIconButton,
  MatButton,
  MatFabButton
} from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { TranslatePipe } from "@ngx-translate/core";

@Component({
  selector: "app-zone-map",
  templateUrl: "./zone-map.component.html",
  styleUrls: ["./zone-map.component.scss"],
  imports: [
    NgxUiLoaderModule,
    MatToolbar,
    MatIconButton,
    MatIcon,
    MatButton,
    MatFabButton,
    TranslatePipe
  ]
})
export class ZoneMapComponent implements OnInit, AfterViewInit {
  ngxLoader = inject(NgxUiLoaderService);
  zoneStore = inject(ZoneStore);
  protected serviceErrorHandler = inject(ServiceErrorHandlerService);
  private clientConfigService = inject(ClientConfigService);
  dialog = inject(MatDialog);

  isAdmin: boolean;
  isLoading: boolean;
  isError: boolean;

  public zoneDialog;
  public addMode: boolean = false;
  public editMode: boolean = false;
  private zoneList: Zone[];
  private selectSingleClickInteraction: Select;
  private modifyInteraction: Modify;
  private map: Map;
  private drawNewInteraction: Draw;
  private mainView: View;
  private vectorSource: VectorSource<any>;
  private tempNewZone: Zone;
  private postcodeMarkerLayer: VectorLayer<any>;

  ngOnInit() {
    this.ngxLoader.startLoader("zoneLoader", "initial");
  }

  ngAfterViewInit(): void {
    this.mainView = new View({
      center: fromLonLat([-0.13947, 50.82]),
      zoom: 12
    });

    this.vectorSource = new VectorSource();

    const config = this.clientConfigService.config;

    const { bingKey, imagerySet, tileUrl } = config.maps;
    this.map = new Map({
      layers: [
        new TileLayer(
          bingKey && bingKey.length > 0
            ? ({
                visible: true,
                preload: Infinity,
                source: new BingMaps({
                  key: bingKey,
                  imagerySet: imagerySet,
                  maxZoom: 19
                })
              } as any)
            : ({
                source: new XYZ({
                  attributions: [
                    '<a href="https://www.openstreetmap.org/copyright">© OpenStreetMap</a> contributors'
                  ],
                  url: tileUrl
                })
              } as any)
        )
      ],
      controls: defaultControls({
        attribution: true
      }).extend([new Attribution({}), new FullScreen(), new ScaleLine()]),
      interactions: defaultInteractions().extend([new DragRotateAndZoom()]),
      target: "map",
      view: this.mainView
    });

    // Create interactions
    this.selectSingleClickInteraction = new Select({});
    this.selectSingleClickInteraction.on(
      "select",
      this.zoneClickHandler.bind(this)
    );

    this.modifyInteraction = new Modify({
      features: this.selectSingleClickInteraction.getFeatures()
    });
    this.modifyInteraction.on("modifyend", this.zoneModifyHandler.bind(this));

    this.drawNewInteraction = new Draw({
      source: this.vectorSource,
      type: "Polygon"
    });
    this.drawNewInteraction.on("drawend", this.zoneDrawEndHandler.bind(this));

    this.map.addInteraction(this.selectSingleClickInteraction);

    // context menu
    // console.log(ContextMenu);
    // let contextmenu = new ContextMenu({
    //   width: 170,
    //   defaultItems: true, // defaultItems are (for now) Zoom In/Zoom Out
    //   items: [
    //     {
    //       text: 'Center map here',
    //       classname: 'some-style-class', // add some CSS rules
    //       callback: this.center // `center` is your callback function
    //     },
    //     {
    //       text: 'Add a Marker',
    //       classname: 'some-style-class', // you can add this icon with a CSS class
    //                                      // instead of `icon` property (see next line)
    //       icon: 'img/marker.png',  // this can be relative or absolute
    //       callback: this.marker
    //     },
    //     '-' // this is a separator
    //   ]
    // });
    // this.map.addControl(contextmenu);

    this.refreshData();
  }

  center() {}

  marker() {}

  refreshData() {
    let layers = this.map.getLayers();
    let toRemove = [];
    layers.forEach((t) => {
      if (t instanceof VectorLayer) {
        console.log("removing layer");
        toRemove.push(t);
      }
    });
    toRemove.forEach((value) => {
      this.map.removeLayer(value);
    });

    this.ngxLoader.startLoader("zoneLoader", "initial");

    this.zoneStore.loadAll().then(this.zoneLoadHandler.bind(this));
    // this.locationStore.loadAll(null, true, 0, 1000).then(this.locationLoadHandler.bind(this));
  }

  addZoneDialog() {}

  locationLoadHandler(locationList: LocationModel[]) {
    console.log("loaded all locations");
    let featureList: any[] = [];
    locationList.forEach((location) => {
      featureList.push(
        new Feature({
          geometry: new Point(
            fromLonLat([location.longitude, location.latitude])
          ),
          // i: 0,
          size: 10
        })
      );
    });

    this.vectorSource.addFeatures(featureList);
    // let source = new VectorSource()
    let vector = new VectorLayer({
      source: this.vectorSource,
      style: new Style({
        image: new CircleStyle({
          radius: 10,
          fill: new Fill({ color: "#666666" }),
          stroke: new Stroke({ color: "#bada55", width: 1 })
        })
      })
    });
    this.map.addLayer(vector);
  }

  zoneLoadHandler(zoneList: ResourceCollection<Zone>) {
    let viewExtent = createEmpty();

    this.zoneList = zoneList.resources;
    zoneList.resources.forEach((value: Zone) => {
      try {
        let feature: Feature<any>[] = new WKT().readFeatures(value.area);
        if (feature.length === 1) {
          feature[0].setId(value.id);
          feature[0].set("description", value.name);
          let extent = feature[0].getGeometry().getExtent();
          console.log("topLeft", toLonLat([extent[0], extent[1]]));
          console.log("bottomRight", toLonLat([extent[2], extent[3]]));

          extend(viewExtent, feature[0].getGeometry().getExtent());
        }

        let source = new VectorSource({
          features: feature
        });

        let fill = value.backgroundColor
          ? value.backgroundColor
          : "rgba(12,12,12,0.5)";
        let stroke = value.foregroundColor
          ? value.foregroundColor
          : "rgba(100,100,100,0.3)";
        // let description = value.name;
        // @ts-ignore
        let vector = new VectorLayer({
          source: source,
          style: new Style({
            fill: new Fill({ color: fill }),
            stroke: new Stroke({
              color: stroke,
              width: 2
            }),
            text: new Text({
              font: '20px Roboto, "Helvetica Neue", sans-serif',
              fill: new Fill({ color: stroke }),
              stroke: new Stroke({
                color: "#000",
                width: 0
              }),

              text: value.name
            })
          })
        });
        this.map.addLayer(vector);
        vector.setZIndex(2147483647 - value.ordinal);
      } catch (e) {
        console.warn("bad zone");
      }
    });
    console.log("fitting main view to ", viewExtent);

    this.ngxLoader.stopLoader("zoneLoader", "initial");
    if (viewExtent[0] === Infinity) {
      return;
    }
    this.mainView.fit(viewExtent, {
      duration: 1000,
      padding: [100, 100, 100, 100]
    });
  }

  writeZone() {
    console.log(
      new WKT().writeFeature(
        this.selectSingleClickInteraction.getFeatures().item(0)
      )
    );
  }

  public zoneModifyHandler(e) {
    if (this.zoneDialog && this.zoneDialog.componentInstance) {
      this.zoneDialog.componentInstance.notifyChange(
        new WKT().writeFeature(e.features.item(0))
      );
    }
  }

  public textFunction(a) {
    console.log("text function ", a);
    return a.get("description");
  }

  zoneClickHandler(e) {
    console.log("selecting");
    console.log(e);
    if (e.selected.length === 1) {
      this.dialog.closeAll();
      this.editMode = true;

      let zoneId = e.selected[0].getId();
      let selectedZone = this.zoneList.find((zone) => zone.id === zoneId);

      let layer = this.selectSingleClickInteraction.getLayer(e.selected[0]);
      layer.setZIndex(Infinity);

      this.zoneDialog = this.dialog.open(ZoneEditOverlayComponent, {
        hasBackdrop: false,
        autoFocus: false,
        width: "300px",
        position: {
          right: "30px",
          bottom: "30px"
        },
        panelClass: "toolbar-popup-dialog",
        data: {
          zone: selectedZone
        }
      });
      this.map.addInteraction(this.modifyInteraction);
      if (this.zoneDialog) {
        this.zoneDialog.afterClosed().subscribe(() => {
          this.editMode = false;
          this.map.removeInteraction(this.modifyInteraction);
          this.selectSingleClickInteraction.getFeatures().clear();

          this.selectSingleClickInteraction.setActive(true);
          this.refreshData();
        });
      }
      this.selectSingleClickInteraction.setActive(false);
      // this.map.removeInteraction(this.selectSingleClickInteraction);
    } else {
      if (this.zoneDialog) {
        this.zoneDialog.close();
      }
    }
  }

  zoneDrawEndHandler(e) {
    this.ngxLoader.startLoader("zoneLoader", "save");

    let zone = new Zone();
    zone.area = new WKT().writeFeature(e.feature);
    zone.name = this.tempNewZone.name;
    this.zoneStore.create(zone).subscribe(
      () => {
        this.ngxLoader.stopLoader("zoneLoader", "save");
        this.addMode = false;
        this.map.addInteraction(this.selectSingleClickInteraction);
        this.map.removeInteraction(this.drawNewInteraction);
        this.refreshData();
      },
      (error) => {
        this.ngxLoader.stopLoader("zoneLoader", "save");
        //this.serviceErrorHandler.handleError(error);
        if (("" + error).includes("GSON")) {
          this.addMode = false;
          this.map.addInteraction(this.selectSingleClickInteraction);
          this.map.removeInteraction(this.drawNewInteraction);
          this.refreshData();
        } else {
          this.addMode = false;
          this.map.addInteraction(this.selectSingleClickInteraction);
          this.map.removeInteraction(this.drawNewInteraction);
          this.serviceErrorHandler.handleError(error);
          this.refreshData();
        }
      }
    );
  }

  openPostcodeLookup() {
    let dialogRef = this.dialog.open(ZonePostcodeLookupComponent, {
      width: "480px",
      maxWidth: "95vw",
      autoFocus: true
    });
    dialogRef.afterClosed().subscribe((result: ZonePostcodeLookupResult) => {
      if (
        result &&
        result.showOnMap &&
        result.longitude != null &&
        result.latitude != null
      ) {
        this.showPostcodeOnMap(
          result.longitude,
          result.latitude,
          result.postcode
        );
      }
    });
  }

  showPostcodeOnMap(longitude: number, latitude: number, label: string) {
    if (this.postcodeMarkerLayer) {
      this.map.removeLayer(this.postcodeMarkerLayer);
    }

    let coordinate = fromLonLat([longitude, latitude]);
    let feature = new Feature({ geometry: new Point(coordinate) });

    this.postcodeMarkerLayer = new VectorLayer({
      source: new VectorSource({ features: [feature] }),
      style: new Style({
        image: new CircleStyle({
          radius: 8,
          fill: new Fill({ color: "#d50000" }),
          stroke: new Stroke({ color: "#ffffff", width: 2 })
        }),
        text: new Text({
          text: label,
          offsetY: -16,
          font: '14px Roboto, "Helvetica Neue", sans-serif',
          fill: new Fill({ color: "#d50000" }),
          stroke: new Stroke({ color: "#ffffff", width: 3 })
        })
      })
    });
    this.postcodeMarkerLayer.setZIndex(Infinity);
    this.map.addLayer(this.postcodeMarkerLayer);

    this.mainView.animate({
      center: coordinate,
      zoom: Math.max(this.mainView.getZoom() ?? 14, 14),
      duration: 600
    });
  }

  addZone() {
    let dialogRef = this.dialog.open(NewZoneConfirmDialogComponent, {});
    dialogRef.afterClosed().subscribe((value) => {
      if (value.draw) {
        this.tempNewZone = value.zone;
        this.addMode = true;
        this.map.removeInteraction(this.selectSingleClickInteraction);
        this.map.addInteraction(this.drawNewInteraction);
      } else if (value.import) {
        console.log("importing from WKT");
      } else {
        console.log("aborted");
      }
    });
  }

  undoLastPoint() {
    this.drawNewInteraction.removeLastPoint();
  }
}
